import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle2, XCircle, User, Info, Landmark, FileText, DollarSign, ShoppingBag } from 'lucide-react';
import CommonModal from '@/components/common/CommonModal';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { Ipayout } from '@/interfaces/Payouts/payout.interface';
import { formatRand, showToastNotification } from '@/utility/helper';
import { StatusBadge } from '@/components/common/StatusBadge';
import { CommonCard } from '../CommonCard';
import FormField from '../FormField';
import { SettlementOrdersModal } from './SettlementOrdersModal';
import { StoreSettlementOrdersModal } from './StoreSettlementOrdersModal';
import MultiImageUploader from '../MultiImageUploader';
import { DetailsGallery } from '@/components/common/DetailsGallery';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';

interface PayoutDetailsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    payout: Ipayout | null;
    onSuccess: () => void;
    type?: "driver" | "store"
}

export const PayoutDetailsModal: React.FC<PayoutDetailsModalProps> = ({
    open,
    onOpenChange,
    payout,
    onSuccess,
    type = 'driver'
}) => {
    const [note, setNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showStoreOrdersHistory, setShowStoreOrdersHistory] = useState(false);
    const [receiptFiles, setReceiptFiles] = useState<(File | string)[]>([]);
    const { canApprove, canApprovedReject } = usePermissions();
    const canPermission = type === "driver" ? canApprovedReject(RESOURCES.PAYOUTS) : canApprove(RESOURCES.STORE_SETTLEMENT);


    useEffect(() => {
        if (open && payout) {
            setNote(payout.note || '');
            setReceiptFiles([]);
        } else {
            setNote('');
            setReceiptFiles([]);
        }
    }, [open, payout]);

    const handleProcessPayout = async (status: 'approved' | 'rejected') => {
        if (!payout) return;

        if (status === 'approved' && receiptFiles.length === 0) {
            showToastNotification('danger', 'Receipt file is required to approve the payout');
            return;
        }

        setActionLoading(true);
        try {
            // if (status == "approved") {
            //     const response = await callApi<IPayoutInitResponse>(
            //         `${API_URL}${API_ENDPOINT.payment}/initiate-payout`,
            //         {
            //             method: "POST",
            //             data: {
            //                 amount: payout?.amount,
            //                 payoutId: payout?.id
            //             }
            //         }
            //     );
            //     console.log("Pay Response: ", response);
            //     if (response?.data.checkoutUrl) {
            //         window.open(response.data.checkoutUrl, '_blank');
            //         onOpenChange(false);
            //         onSuccess();
            //         return;
            //     }
            // } else {
            const formData = new FormData();
            formData.append('status', status);
            formData.append('note', note);
            if (status === 'approved' && receiptFiles.length > 0) {
                const file = receiptFiles[0];
                if (typeof file !== 'string') {
                    formData.append('file', file);
                }
            }

            await callApi(
                `${API_URL}${API_ENDPOINT.payouts}/process/${payout.id}`,
                {
                    method: 'PATCH',
                    multipart: true,
                    data: formData
                }
            );
            // }
            showToastNotification('success', `Payout request successfully ${status}`);
            onOpenChange(false);
            onSuccess();
        } catch (error) {
            ThrowError(error);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <CommonModal
            open={open}
            onOpenChange={onOpenChange}
            title="Payout Request Details"
            size="xl"
        >
            <div className="space-y-4 p-2 ">
                {/* Payout Summary */}
                <CommonCard title="Payout Summary" icon={<CreditCard />} iconColor="#EF4444">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-2xl bg-secondary/20 border border-secondary/20 space-y-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" /> {payout?.store ? 'Store Information' : 'Driver Information'}
                            </h3>
                            <div className="space-y-1">
                                {payout?.store ? (
                                    <>
                                        <p className="text-sm font-bold text-foreground">{payout?.store?.name || '---'}</p>
                                        <p className="text-xs text-muted-foreground">{payout?.store?.user?.fullName || '---'}</p>
                                        <p className="text-xs text-muted-foreground">{payout?.store?.user?.email || '---'}</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-bold text-foreground">{payout?.driver?.fullName || '---'}</p>
                                        <p className="text-xs text-muted-foreground">{payout?.driver?.phone || '---'}</p>
                                        <p className="text-xs text-muted-foreground">{payout?.driver?.email || '---'}</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="p-4 rounded-2xl bg-secondary/20 border border-secondary/20 space-y-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <DollarSign className="w-3.5 h-3.5" /> Request Summary
                            </h3>
                            <div className="space-y-1.5">
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">Requested Amount:</span>
                                    <span className="font-bold text-foreground">{formatRand(payout?.amount)}</span>
                                </div>
                                {type == "driver" && <div className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">Wallet Balance at Request:</span>
                                    <span className="font-bold text-foreground">{formatRand(payout?.walletBalanceAtRequest)}</span>
                                </div>}
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">Status:</span>
                                    <StatusBadge status={payout?.status} />
                                </div>
                                {type === "store" && (
                                    <div className="pt-2 border-t border-secondary/10">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowStoreOrdersHistory(true)}
                                            className="w-full text-xs h-8 gap-1.5 font-bold"
                                            leftIcon={<ShoppingBag className="w-3.5 h-3.5" />}
                                        >
                                            View Included Orders
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </CommonCard>

                {/* Bank Details */}
                {(() => {
                    const wallet = payout?.store ? payout.store.user?.wallet : payout?.driver?.wallet;
                    const entityName = payout?.store ? "store" : "driver";
                    return (
                        <CommonCard title="Destination Bank Account" icon={<Landmark />} iconColor="#3B82F6">
                            {wallet ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <p className="text-[10px] uppercase font-bold text-muted-foreground">Account Holder Name</p>
                                        <p className="text-sm font-semibold text-foreground">{wallet.accountHolderName || '---'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-muted-foreground">Bank Name</p>
                                        <p className="text-sm font-semibold text-foreground">{wallet.bankName || '---'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-muted-foreground">Account Number</p>
                                        <p className="text-sm font-semibold text-foreground">{wallet.accountNumber || '---'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-muted-foreground">Account Type</p>
                                        <p className="text-sm font-semibold text-foreground">{wallet.accountType || '---'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-muted-foreground">Branch Name</p>
                                        <p className="text-sm font-semibold text-foreground">{wallet.branchName || '---'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-muted-foreground">Branch Code</p>
                                        <p className="text-sm font-semibold text-foreground">
                                            {wallet.branchCode || '---'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 rounded-2xl border border-warning/20 bg-warning/5 text-warning flex items-start gap-2.5">
                                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-bold">No Bank Account Found</p>
                                        <p className="text-[11px] opacity-80">This {entityName} does not have a bank account configured in their wallet profile.</p>
                                    </div>
                                </div>
                            )}
                        </CommonCard>
                    );
                })()}

                {/* Action note */}
                <CommonCard title="Admin Remarks / Note" icon={<FileText />} iconColor="#10B981">
                    {payout?.status === 'pending' ? (
                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <FormField
                                    name="note"
                                    type='textarea'
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    disabled={actionLoading}
                                    placeholder="Add transaction reference ID, payment details, or reject reason..."
                                />
                                <p className="text-[10px] text-muted-foreground px-1">
                                    This note will be visible to the driver in their transaction history.
                                </p>
                            </div>

                            <div className="space-y-1.5 pt-2 border-t border-secondary/10">
                                <MultiImageUploader
                                    value={receiptFiles}
                                    onChange={setReceiptFiles}
                                    label="Payment Receipt"
                                    maxFiles={1}
                                    type=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                                />
                                {/* <p className="text-[10px] text-muted-foreground px-1">
                                    Upload proof of payment
                                </p> */}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 rounded-2xl bg-secondary/10 border border-secondary/10 text-sm text-foreground">
                                {payout?.note ? (
                                    <p className="italic">"{payout.note}"</p>
                                ) : (
                                    <p className="text-muted-foreground italic">No note was provided.</p>
                                )}
                            </div>

                            {payout?.receiptUrl && (
                                <div className="pt-2 border-t border-secondary/10">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Payment Receipt</h4>
                                    <DetailsGallery
                                        media={[{ url: typeof payout.receiptUrl === 'string' ? payout.receiptUrl : (payout.receiptUrl as any).url || payout.receiptUrl }]}
                                        name={`Receipt for Payout ${payout.id}`}
                                        className="w-1/2 sm:w-1/3 p-0 border-0 shadow-none bg-transparent"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </CommonCard>

                {/* Footer Actions */}
                {(() => {
                    const wallet = payout?.store ? payout.store.user?.wallet : payout?.driver?.wallet;
                    return (
                        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-secondary/10 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={actionLoading}
                                className="rounded-xl flex-1 h-10"
                            >
                                Cancel
                            </Button>
                            {payout?.status === 'pending' && (
                                <>
                                    {!(payout?.store) && (
                                        <Button
                                            variant="destructive"
                                            onClick={() => handleProcessPayout('rejected')}
                                            isLoading={actionLoading}
                                            className="rounded-xl flex-1 gap-2 h-10"
                                            leftIcon={<XCircle className="w-4 h-4" />}
                                        >
                                            Reject Request
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={() => handleProcessPayout('approved')}
                                        isLoading={actionLoading}
                                        disabled={!wallet}
                                        className="rounded-xl flex-1 gap-2 h-10 disabled:opacity-50"
                                        leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                    >
                                        Approve & Pay
                                    </Button>
                                </>
                            )}
                        </div>
                    );
                })()}
            </div>

            <SettlementOrdersModal
                open={showHistory}
                onOpenChange={setShowHistory}
                driverId={payout?.driver?.id || null}
                // settlementId={payout?.id || null}
                cashSettlementStatus='settled'
            />

            <StoreSettlementOrdersModal
                open={showStoreOrdersHistory}
                onOpenChange={setShowStoreOrdersHistory}
                storeId={payout?.store?.id || payout?.storeId || null}
                startDate={payout?.startDate || null}
                endDate={payout?.endDate || null}
            />
        </CommonModal>
    );
};
